<template>
	<article class="customer-reviews">
		<template v-if="aggregatedRating && customerReviews.length">
			<h2 class="customer-reviews__title">Gästebewertung</h2>
			<CustomerReviewsHeader
				class="customer-reviews__header"
				:aggregated-rating="aggregatedRating"
			/>
			<section class="customer-reviews__list">
				<ReviewBox
					v-for="(review, index) in customerReviews"
					:key="review.Title + index"
					class="customer-reviews__item"
					:review="review"
					@show-detail-review="onShowDetailReview(index + 1)"
				/>
			</section>
		</template>

		<div class="pager">
			<Loading
				v-if="isLoading"
				size="medium"
				class="pager__loader"
			/>

			<RoundedButton
				v-if="showFetchMoreButton"
				class="customer-reviews__load-more"
				theme="outlined"
				@click="fetchMore()"
			>
				Mehr Bewertungen anzeigen
			</RoundedButton>
		</div>
	</article>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import * as hotelReviewsService from '@services/hotelReviewsService';
import Loading from '@lmt-rpb/Loading/Loading.vue';
import RoundedButton from '@lmt-rpb/RoundedButton/RoundedButton.vue';
import ReviewBox from '@lmt-rpb/ReviewBox/ReviewBox.vue';
import CustomerReviewsHeader from './internals/CustomerReviewsHeader.vue';
import type { AggregateRatingType, CustomerReviewType, ReviewType } from '@/interfaces/review-types';
import { logErrorResponse } from '@/js/utils/utils';
import SelectContentAnalytics from '@/js/modules/analytics/SelectContentAnalytics';
import { storeToRefs } from 'pinia';
import { useBreakpointStore } from 'src/store/breakpointsStore';

interface Props {
	iffCode: number;
	hotelName: string;
}

const emit = defineEmits(['no-reviews']);

const props = defineProps<Props>();
const { isMinTablet } = storeToRefs(useBreakpointStore());
const reviewsPerPage = computed((): number => (isMinTablet.value ? 4 : 3));
const aggregatedRating = ref<AggregateRatingType | null>(null);
const customerReviews = ref<ReviewType[]>([]);
const nextUrl = ref<string>('');
const isLastPage = computed(() => !nextUrl.value);
const isLoading = ref<boolean>(true);
const showFetchMoreButton = computed((): boolean => !isLoading.value && !!customerReviews.value.length && !isLastPage.value);

async function fetchInitial() {
	const resp = await fetchItems();

	if (resp?.AggregateRating) {
		// only set aggregatedRating once on initial load
		aggregatedRating.value = resp.AggregateRating;
	}

	if (resp) {
		assignResponseValues(resp);
	}

	if (!(aggregatedRating.value && customerReviews.value.length)) {
		emit('no-reviews', true);
	}
}

const loadMoreAnalytics = () => {
	SelectContentAnalytics.selectContent({ cId: 'reviews_button', cType: 'ux_hotelpage_load_more' });
};

async function fetchMore() {
	loadMoreAnalytics();
	const resp = await fetchItems();
	if (resp) {
		assignResponseValues(resp);
	}
}

function assignResponseValues(resp: CustomerReviewType) {
	customerReviews.value.push(...resp.CustomerReviews);
	nextUrl.value = resp?._links?.next ?? '';
}

async function fetchItems() {
	isLoading.value = true;
	let resp: CustomerReviewType | null = null;
	try {
		resp = await hotelReviewsService.get(props.iffCode, reviewsPerPage.value, nextUrl.value);
	} catch (error) {
		logErrorResponse('customer reviews request error', error, { params: [props.iffCode, reviewsPerPage.value, nextUrl.value] }, resp);
	} finally {
		isLoading.value = false;
	}

	return resp;
}

const onShowDetailReview = (index: number) => {
	SelectContentAnalytics.selectContent({ cId: 'review_item', index, cType: 'ux_hotelpage_show_details' });
};

onMounted(() => {
	fetchInitial();
});
</script>

<style lang="scss" scoped>
.customer-reviews {
	max-width: $breakpoint-container;
	margin: 0 auto;
	padding: 0;

	&__list {
		display: grid;
		justify-items: center;
		gap: 1.6rem;

		@include media-query-up($breakpoint-small) {
			grid-template-columns: repeat(2, 1fr);
		}
	}

	&__title {
		font-size: $font-medium-3;
		font-weight: $font-weight-semibold;
		line-height: 4.5rem;
		text-align: left;
		padding: 0 1.6rem;
		margin: 0;

		@include media-query-up($breakpoint-small) {
			padding: 0;
			font-size: $font-medium-2;
			margin-bottom: 1.6rem;
		}

		@media (min-width: $breakpoint-large) {
			font-size: $font-large-2;
			font-weight: $font-weight-bold;
		}
	}

	&__header {
		padding: 0 1.6rem;

		@include media-query-up($breakpoint-small) {
			padding: 0 1.6rem;
		}

		@media (min-width: $breakpoint-large) {
			padding: 0 1.6rem;
		}
	}

	&__item {
		width: 100%;
	}

	&__load-more {
		white-space: nowrap;
		font-size: $font-small-2;
		padding: 0.8rem 2.4rem;
	}
}

.pager {
	padding-top: 2.4rem;
	text-align: center;

	&__loader {
		margin: 0 auto;
	}
}
</style>
